import { motion } from 'framer-motion';
import { Shield, Clock, Database, Mail, Lock, Eye } from 'lucide-react';

const sections = [
  {
    id: 'data-collection',
    icon: Database,
    title: 'Data Collection',
    content: [
      'Direct message conversations with the AI Coach bot',
      'User profile information (name and Slack user ID)',
      'Practice metadata (timestamps, session duration)',
      'Leadership development preferences and goals',
      'Role-play conversation transcripts',
    ],
  },
  {
    id: 'data-usage',
    icon: Eye,
    title: 'Data Usage',
    content: [
      'Providing personalized leadership coaching',
      'Generating contextual responses and feedback',
      'Maintaining conversation context for better coaching',
      'Improving coaching quality through aggregated insights',
      'System monitoring and security',
    ],
  },
  {
    id: 'data-retention',
    icon: Clock,
    title: 'Data Retention',
    content: [
      'Conversation logs are stored for 30 days by default',
      'Customizable retention periods available for enterprise customers',
      'Basic account information retained while account is active',
      'Automated data deletion after retention period',
      'Option for immediate data deletion upon request',
    ],
  },
  {
    id: 'data-security',
    icon: Lock,
    title: 'Security Measures',
    content: [
      'End-to-end encryption using TLS 1.3',
      'AES-256 encryption for stored data',
      'Regular security audits and penetration testing',
      'Multi-tenant architecture with strict data isolation',
      'Comprehensive audit logging and monitoring',
    ],
  },
];

export default function SlackPrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Shield className="h-16 w-16 text-primary mx-auto mb-8" />
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Privacy Policy
            </h1>
            <p className="mt-6 text-lg text-gray-600 max-w-2xl mx-auto">
              We are committed to protecting your privacy and ensuring the
              security of your data. This policy explains how we collect, use,
              and protect your information when using the Hupo AI Coach for
              Slack.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-2">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-8 shadow-sm"
            >
              <section.icon className="h-8 w-8 text-primary mb-6" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                {section.title}
              </h2>
              <ul className="space-y-4">
                {section.content.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/5 flex items-center justify-center mt-0.5 mr-3">
                      <span className="text-primary text-sm">•</span>
                    </span>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Data Rights Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 bg-white rounded-xl p-8 shadow-sm"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Your Data Rights
          </h2>
          <p className="text-gray-600 mb-6">
            You have the right to access, transfer, or delete your data at any
            time. To exercise these rights:
          </p>
          <ul className="space-y-4 mb-8">
            <li className="flex items-start">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/5 flex items-center justify-center mt-0.5 mr-3">
                <span className="text-primary text-sm">1</span>
              </span>
              <span className="text-gray-600">
                Email us at privacy@hupo.co with your request
              </span>
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/5 flex items-center justify-center mt-0.5 mr-3">
                <span className="text-primary text-sm">2</span>
              </span>
              <span className="text-gray-600">
                Use the /privacy command in Slack to view or request data
                deletion
              </span>
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/5 flex items-center justify-center mt-0.5 mr-3">
                <span className="text-primary text-sm">3</span>
              </span>
              <span className="text-gray-600">
                Contact your workspace administrator for organization-wide data
                management
              </span>
            </li>
          </ul>

          <p className="text-gray-600">
            We will respond to all data-related requests within 30 days. For
            urgent matters or additional questions about your privacy, please
            contact our Data Protection Officer at bek@hupo.co
          </p>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <Mail className="h-8 w-8 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Contact Us
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            For any privacy-related questions or concerns, please contact us at{' '}
            <a
              href="mailto:privacy@hupo.co"
              className="text-primary hover:underline"
            >
              privacy@hupo.co
            </a>
            . We are committed to addressing your concerns and ensuring the
            protection of your data.
          </p>
        </motion.div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          Last updated: November 19, 2024
        </div>
      </div>
    </div>
  );
}
